import { entityEffects, selectCurrentEntity } from '@opengov/app-core/entity';
import { redirectToUserEntity } from '@opengov/app-core/routes/onEnterFunctions';
import { canSwitchEntities, getSession, getSessionEntitySubdomain } from '@opengov/app-core/session/selectors';
import { routerActions } from '@opengov/compose-app-router/state';
import { IOnEnterFunction } from '@opengov/compose-app-router/types';
import { STORIES_VIEWER_PRIVILEGE } from '@opengov/stories-shared/constants';
import { IEntity } from '@opengov/og-api-ts';

import { fetchCurrentSubjectClaims } from '../stories/api/subjectClaimsApi';
import { hasPrivilege } from '../stories/utils/helper';
import { ISubjectClaims } from '../types';
import { storyListPath } from './paths';

// In PSP mode the active entity is owned by the PSP shell and handed to us via
// pspOptions.activeEntity. pspBootstrap.ts stashes it on
// window.__OG_STORIES_PSP_OPTIONS__ before any stories code evaluates, so
// route onEnter hooks (which fire after compose-app-router matches a URL) can
// read it synchronously.
//
// We prefer the PSP entity over Redux session state because PSP routes us to
// bare `/stories` (no entitySubdomain segment) on navbar navigation. Without
// this override, ensureEntity falls back to redirectToUserEntity, which reads
// from Redux session state — but the session-fetch flow expects an
// entitySubdomain in the URL to start with. Result: route matches `/stories`
// but redirect never resolves and the page hangs.
//
// Standalone build (window.__OG_STORIES_PSP_OPTIONS__ is undefined) keeps the
// existing redirectToUserEntity path unchanged.
function getPspActiveEntityFriendlyId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const opts = (window as { __OG_STORIES_PSP_OPTIONS__?: { activeEntity?: { friendlyId?: string } } })
    .__OG_STORIES_PSP_OPTIONS__;
  return opts?.activeEntity?.friendlyId;
}

// compose-app-router types onEnter arguments as `any`; type the ones this guard uses.
type AppState = Parameters<typeof getSessionEntitySubdomain>[0];
interface IStoriesRouteLocation {
  params: { entitySubdomain?: string };
}

/**
 * True when the user holds STORIES_VIEWER_PRIVILEGE in the entity named by the URL.
 *
 * Users who belong to several entities (e.g. guest users) never get the Delphius `switch_to`
 * ability for their non-home entities, so `switch_to` alone would always send them back to their
 * session entity. The stories claim is the authoritative per-entity grant, so it decides instead.
 */
async function hasStoriesAccessToEntity(state: AppState, requestedEntitySubdomain: string): Promise<boolean> {
  try {
    const entity: Pick<IEntity, 'subdomain' | 'uuid'> = await entityEffects.fetchRequestedOrCurrentEntity({
      requestedEntitySubdomain,
      currentEntity: selectCurrentEntity(state)
    });
    // fetchRequestedOrCurrentEntity silently falls back to the current entity when the requested
    // one cannot be fetched; never grant access to the requested entity based on another one's claims.
    if (!entity?.uuid || entity.subdomain !== requestedEntitySubdomain) {
      return false;
    }
    const claims = await fetchCurrentSubjectClaims({ userUuid: getSession(state).uuid, entityUuid: entity.uuid });
    return hasPrivilege(claims as ISubjectClaims, STORIES_VIEWER_PRIVILEGE);
  } catch {
    return false;
  }
}

export const onEnterStoriesRoutes: IOnEnterFunction = async (
  state: AppState,
  nextLocation: IStoriesRouteLocation,
  onSuccess: () => void,
  onError: (message: string, action: unknown) => void
): Promise<void> => {
  const requestedEntitySubdomain = nextLocation.params.entitySubdomain;
  const userEntitySubdomain: string = getSessionEntitySubdomain(state);

  if (canSwitchEntities(state) || requestedEntitySubdomain === userEntitySubdomain) {
    onSuccess();
    return;
  }

  if (requestedEntitySubdomain && (await hasStoriesAccessToEntity(state, requestedEntitySubdomain))) {
    onSuccess();
    return;
  }

  onError('Redirecting...', routerActions.replace.create({ pathname: storyListPath(userEntitySubdomain) }));
};

const standaloneRedirect = redirectToUserEntity((userEntitySubdomain) =>
  routerActions.replace.create({ pathname: storyListPath(userEntitySubdomain) })
);

export const ensureEntity = {
  title: 'Stories',
  onEnter: (state, nextLocation, onSuccess, onError) => {
    const pspEntity = getPspActiveEntityFriendlyId();
    if (pspEntity) {
      return onError(
        'Redirecting to PSP active entity',
        routerActions.replace.create({ pathname: storyListPath(pspEntity) })
      );
    }
    return standaloneRedirect(state, nextLocation, onSuccess, onError);
  }
};
